/**
 * Centralized logging utility that combines console logging with Sentry tracking
 * Provides consistent error handling across the application
 */

import * as Sentry from '@sentry/nextjs'

export interface LogContext {
	[key: string]: unknown
}

/**
 * Logs an error message to console (dev only) and Sentry
 * @param message - Error message
 * @param context - Additional context for debugging
 */
export function logError(message: string, context?: LogContext): void {
	if (process.env.NODE_ENV !== 'production') {
		console.error(`[Error] ${message}`, context)
	}

	Sentry.captureMessage(message, {
		level: 'error',
		extra: context,
	})
}

/**
 * Logs a warning message to console (dev only) and Sentry
 * @param message - Warning message
 * @param context - Additional context for debugging
 */
export function logWarning(message: string, context?: LogContext): void {
	if (process.env.NODE_ENV !== 'production') {
		console.warn(`[Warning] ${message}`, context)
	}

	Sentry.captureMessage(message, {
		level: 'warning',
		extra: context,
	})
}

/**
 * Logs an info message to console (dev only) and Sentry
 * @param message - Info message
 * @param context - Additional context for debugging
 */
export function logInfo(message: string, context?: LogContext): void {
	if (process.env.NODE_ENV !== 'production') {
		console.log(`[Info] ${message}`, context)
	}

	Sentry.captureMessage(message, {
		level: 'info',
		extra: context,
	})
}

/**
 * Logs an exception to console (dev only) and Sentry
 * @param error - Error object or message
 * @param context - Additional context for debugging
 */
export function logException(error: Error | string, context?: LogContext): void {
	const errorObj = error instanceof Error ? error : new Error(error)

	if (process.env.NODE_ENV !== 'production') {
		console.error(`[Exception] ${errorObj.message}`, context)
	}

	Sentry.captureException(errorObj, {
		extra: context,
	})
}

/**
 * Logs a debug message (console only, not sent to Sentry)
 * Only logs in development mode
 * @param message - Debug message
 * @param data - Data to log
 */
export function logDebug(message: string, data?: unknown): void {
	if (process.env.NODE_ENV !== 'production') {
		console.log(`[Debug] ${message}`, data)
	}
}
